import { sanitizeForAnalytics } from './apiError';
/**
 * Google Tag Manager Analytics Service
 * Centralized tracking for the Property Management Platform
 * 
 * Usage:
 *   import { analytics } from '@/lib/analytics';
 *   analytics.trackEvent('button_click', { button_name: 'Create Booking' });
 */

// Type definitions for dataLayer
declare global {
    interface Window {
        dataLayer: DataLayerEvent[];
    }
}

interface DataLayerEvent {
    event: string;
    [key: string]: any;
}

// ==================== CORE TRACKING ====================

/**
 * Push an event to the Google Tag Manager dataLayer
 */
function pushToDataLayer(event: DataLayerEvent): void {
    try {
        window.dataLayer = window.dataLayer || [];
        // Nunca objetos de error, detail_obj ni params del catalogo (Seguridad).
        window.dataLayer.push(sanitizeForAnalytics(event as unknown as Record<string, unknown>) as unknown as DataLayerEvent);
    } catch {
        console.warn('[Analytics] Failed to push event');
    }
}

/**
 * Track a custom event
 */
function trackEvent(eventName: string, params: Record<string, any> = {}): void {
    pushToDataLayer({
        event: eventName,
        ...params,
        timestamp: new Date().toISOString(),
    });
}

// ==================== PAGE TRACKING ====================

/**
 * Track a virtual page view (for SPA navigation)
 */
function trackPageView(pageName: string, pageUrl: string): void {
    pushToDataLayer({
        event: 'page_view',
        page_name: pageName,
        page_url: pageUrl,
        page_title: document.title,
        timestamp: new Date().toISOString(),
    });
}

// ==================== USER TRACKING ====================

interface UserProperties {
    userId?: string;
    userRole?: string;
    tenantId?: string;
    tenantName?: string;
}

/**
 * Set user properties for all subsequent events
 */
function setUserProperties(properties: UserProperties): void {
    pushToDataLayer({
        event: 'user_properties_set',
        user_id: properties.userId,
        user_role: properties.userRole,
        tenant_id: properties.tenantId,
        tenant_name: properties.tenantName,
    });
}

/**
 * Track user login
 */
function trackLogin(userId: string, userRole: string, method: string = 'email'): void {
    pushToDataLayer({
        event: 'login',
        user_id: userId,
        user_role: userRole,
        login_method: method,
        timestamp: new Date().toISOString(),
    });
}

/**
 * Track user logout
 */
function trackLogout(): void {
    pushToDataLayer({
        event: 'logout',
        timestamp: new Date().toISOString(),
    });
}

/**
 * Track user registration/onboarding
 */
function trackSignup(step: string, data: Record<string, any> = {}): void {
    pushToDataLayer({
        event: 'signup',
        signup_step: step,
        ...data,
        timestamp: new Date().toISOString(),
    });
}

// ==================== BOOKING TRACKING ====================

interface BookingEventData {
    bookingId?: string;
    propertyId?: string;
    propertyName?: string;
    guestName?: string;
    checkIn?: string;
    checkOut?: string;
    source?: string;
    totalAmount?: number;
}

function trackBookingCreated(data: BookingEventData): void {
    pushToDataLayer({
        event: 'booking_created',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackBookingUpdated(data: BookingEventData): void {
    pushToDataLayer({
        event: 'booking_updated',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackBookingCancelled(data: BookingEventData): void {
    pushToDataLayer({
        event: 'booking_cancelled',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackBookingViewed(data: BookingEventData): void {
    pushToDataLayer({
        event: 'booking_viewed',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

// ==================== PROPERTY TRACKING ====================

interface PropertyEventData {
    propertyId?: string;
    propertyName?: string;
    propertyType?: string;
    location?: string;
}

function trackPropertyCreated(data: PropertyEventData): void {
    pushToDataLayer({
        event: 'property_created',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackPropertyUpdated(data: PropertyEventData): void {
    pushToDataLayer({
        event: 'property_updated',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackPropertyViewed(data: PropertyEventData): void {
    pushToDataLayer({
        event: 'property_viewed',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

// ==================== TASK TRACKING ====================

interface TaskEventData {
    taskId?: string;
    taskTitle?: string;
    taskType?: string;
    propertyId?: string;
    assigneeId?: string;
    status?: string;
    priority?: string;
}

function trackTaskCreated(data: TaskEventData): void {
    pushToDataLayer({
        event: 'task_created',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackTaskCompleted(data: TaskEventData): void {
    pushToDataLayer({
        event: 'task_completed',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackTaskUpdated(data: TaskEventData): void {
    pushToDataLayer({
        event: 'task_updated',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackTaskViewed(data: TaskEventData): void {
    pushToDataLayer({
        event: 'task_viewed',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

// ==================== EMPLOYEE TRACKING ====================

interface EmployeeEventData {
    employeeId?: string;
    role?: string;
}

function trackEmployeeCreated(data: EmployeeEventData): void {
    pushToDataLayer({
        event: 'employee_created',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackEmployeeUpdated(data: EmployeeEventData): void {
    pushToDataLayer({
        event: 'employee_updated',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

// ==================== INVOICE TRACKING ====================

interface InvoiceEventData {
    invoiceId?: string;
    amount?: number;
    status?: string;
    propertyId?: string;
}

function trackInvoiceCreated(data: InvoiceEventData): void {
    pushToDataLayer({
        event: 'invoice_created',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackInvoiceSent(data: InvoiceEventData): void {
    pushToDataLayer({
        event: 'invoice_sent',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

function trackInvoicePaid(data: InvoiceEventData): void {
    pushToDataLayer({
        event: 'invoice_paid',
        ...data,
        timestamp: new Date().toISOString(),
    });
}

// ==================== UI INTERACTION TRACKING ====================

function trackButtonClick(buttonName: string, context: string = ''): void {
    pushToDataLayer({
        event: 'button_click',
        button_name: buttonName,
        context: context,
        page_url: window.location.pathname,
        timestamp: new Date().toISOString(),
    });
}

function trackModalOpen(modalName: string): void {
    pushToDataLayer({
        event: 'modal_open',
        modal_name: modalName,
        page_url: window.location.pathname,
        timestamp: new Date().toISOString(),
    });
}

function trackModalClose(modalName: string): void {
    pushToDataLayer({
        event: 'modal_close',
        modal_name: modalName,
        page_url: window.location.pathname,
        timestamp: new Date().toISOString(),
    });
}

function trackFormSubmit(formName: string, success: boolean): void {
    pushToDataLayer({
        event: 'form_submit',
        form_name: formName,
        success: success,
        page_url: window.location.pathname,
        timestamp: new Date().toISOString(),
    });
}

function trackSearch(searchTerm: string, resultsCount: number, context: string = ''): void {
    pushToDataLayer({
        event: 'search',
        search_term: searchTerm,
        results_count: resultsCount,
        context: context,
        page_url: window.location.pathname,
        timestamp: new Date().toISOString(),
    });
}

function trackFilter(filterName: string, filterValue: string, context: string = ''): void {
    pushToDataLayer({
        event: 'filter_applied',
        filter_name: filterName,
        filter_value: filterValue,
        context: context,
        page_url: window.location.pathname,
        timestamp: new Date().toISOString(),
    });
}

// ==================== ERROR TRACKING ====================

function trackError(errorMessage: string, errorType: string = 'unknown', context: string = ''): void {
    pushToDataLayer({
        event: 'error',
        error_message: errorMessage,
        error_type: errorType,
        context: context,
        page_url: window.location.pathname,
        timestamp: new Date().toISOString(),
    });
}

// ==================== FEATURE USAGE TRACKING ====================

function trackFeatureUsed(featureName: string, details: Record<string, any> = {}): void {
    pushToDataLayer({
        event: 'feature_used',
        feature_name: featureName,
        ...details,
        page_url: window.location.pathname,
        timestamp: new Date().toISOString(),
    });
}

// ==================== EXPORT ====================

export const analytics = {
    // Core
    trackEvent,
    pushToDataLayer,

    // Pages
    trackPageView,

    // Users
    setUserProperties,
    trackLogin,
    trackLogout,
    trackSignup,

    // Bookings
    trackBookingCreated,
    trackBookingUpdated,
    trackBookingCancelled,
    trackBookingViewed,

    // Properties
    trackPropertyCreated,
    trackPropertyUpdated,
    trackPropertyViewed,

    // Tasks
    trackTaskCreated,
    trackTaskCompleted,
    trackTaskUpdated,
    trackTaskViewed,

    // Employees
    trackEmployeeCreated,
    trackEmployeeUpdated,

    // Invoices
    trackInvoiceCreated,
    trackInvoiceSent,
    trackInvoicePaid,

    // UI
    trackButtonClick,
    trackModalOpen,
    trackModalClose,
    trackFormSubmit,
    trackSearch,
    trackFilter,

    // Errors
    trackError,

    // Features
    trackFeatureUsed,
};

export default analytics;
